import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RotateCcw, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { formatBizDateTime } from "../../lib/formatBiz";

const STATUS_STYLES = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  refunded: "bg-slate-100 text-slate-700 border-slate-300",
  forfeited: "bg-slate-100 text-slate-600 border-slate-300 line-through",
  expired: "bg-slate-100 text-slate-500 border-slate-300",
};

function usd(cents) { return `$${((cents || 0) / 100).toFixed(2)}`; }

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payments");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load payments");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refund = async (id) => {
    if (!window.confirm("Refund this payment in full? This cannot be undone from the app.")) return;
    setRefunding(id);
    try {
      await api.post(`/payments/${id}/refund`);
      toast.success("Refund issued");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refund failed");
    } finally { setRefunding(""); }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const filtered = filter === "all" ? rows : rows.filter(r => r.payment_status === filter);
  const totals = rows.reduce((a, r) => {
    if (r.payment_status === "paid") a.paid += r.amount_cents;
    if (r.payment_status === "refunded") a.refunded += r.refunded_amount_cents || r.amount_cents;
    return a;
  }, { paid: 0, refunded: 0 });

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-[Outfit] font-extrabold text-3xl text-[color:var(--owc-navy)]">Payments</h1>
          <p className="text-slate-600 mt-1">Deposits, balance payments, and refunds — all sourced from Stripe.</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div className="owc-card px-4 py-2"><span className="text-xs text-slate-500 uppercase tracking-widest">Collected</span> <span className="ml-2 font-bold text-[color:var(--owc-navy)]" data-testid="payments-total-collected">{usd(totals.paid)}</span></div>
          <div className="owc-card px-4 py-2"><span className="text-xs text-slate-500 uppercase tracking-widest">Refunded</span> <span className="ml-2 font-bold text-slate-700" data-testid="payments-total-refunded">{usd(totals.refunded)}</span></div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "paid", "pending", "refunded", "forfeited", "failed"].map(f => (
          <button
            key={f}
            data-testid={`payments-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-subhead uppercase tracking-wider border transition-colors ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}
          >{f}</button>
        ))}
      </div>

      <div className="owc-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
              <th className="py-3 px-4">Created</th>
              <th className="py-3 px-4">Customer</th>
              <th className="py-3 px-4">Kind</th>
              <th className="py-3 px-4">Amount</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-slate-400" /></td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-slate-500">No payments{filter !== "all" ? ` in "${filter}"` : ""}.</td></tr>
            )}
            {filtered.map(p => (
              <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                <td className="py-3 px-4 text-slate-600">{formatBizDateTime(p.created_at)}</td>
                <td className="py-3 px-4">
                  <div className="font-semibold text-[color:var(--owc-navy)]">{p.customer_name || "—"}</div>
                  <div className="text-xs text-slate-500">{p.customer_email || ""}</div>
                </td>
                <td className="py-3 px-4"><span className="text-xs font-subhead uppercase tracking-wider text-slate-600">{p.kind}</span></td>
                <td className="py-3 px-4 font-semibold text-[color:var(--owc-navy)]">{usd(p.amount_cents)}</td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border ${STATUS_STYLES[p.payment_status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{p.payment_status}</span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex flex-wrap gap-1">
                    {p.checkout_url && p.payment_status === "pending" && (
                      <>
                        <a data-testid={`payment-open-${p.id}`} href={p.checkout_url} target="_blank" rel="noreferrer" className="owc-btn-outline text-xs inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" />Open</a>
                        <button data-testid={`payment-copy-${p.id}`} onClick={() => copyLink(p.checkout_url)} className="owc-btn-outline text-xs inline-flex items-center gap-1"><Copy className="h-3 w-3" />Copy link</button>
                      </>
                    )}
                    {p.payment_status === "paid" && (
                      <button data-testid={`payment-refund-${p.id}`} disabled={refunding === p.id} onClick={() => refund(p.id)} className="owc-btn-outline text-xs inline-flex items-center gap-1">
                        {refunding === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Refund
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
